import { useCallback, useMemo } from 'react';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useOverlayContext } from '@providers/OverlayProvider';
import { WebsocketAction } from '@interfaces/webSocketConnectionInfo';
import { IMap, ITeleportPreview } from '@interfaces/map';
import { AnalyticsType } from '@interfaces/analytics';
import { IRoom } from '@interfaces/rooms';

import { useTeleportModal } from '../context/context';
import {
  shouldDebounceTeleportOpen,
  teleportOpenGuard,
} from '../teleportOpenGuard';

const collectTeleportPreviewsFromMaps = (
  maps: Pick<IMap, 'teleportPreviews'>[] | undefined,
): ITeleportPreview[] => {
  if (!maps?.length) {
    return [];
  }

  const previews: ITeleportPreview[] = [];
  maps.forEach((map) => {
    if (map?.teleportPreviews?.length) {
      previews.push(
        ...map.teleportPreviews.filter((preview) => preview.showInUi !== false),
      );
    }
  });

  return previews.sort(
    (a, b) => (a.positionInWidget ?? 0) - (b.positionInWidget ?? 0),
  );
};

const resolveRoomMap = (room: IRoom, eventMaps?: IMap[]): IMap | undefined => {
  const roomMap = room.map;
  if (!roomMap) {
    return undefined;
  }

  if (roomMap.teleportPreviews?.length) {
    return roomMap;
  }

  const mapId = roomMap.mapId;
  if (!mapId || !eventMaps?.length) {
    return roomMap;
  }

  const eventMap = eventMaps.find((map) => map.mapId === mapId);
  if (!eventMap) {
    return roomMap;
  }

  return { ...roomMap, ...eventMap };
};

const collectTeleportPreviews = (
  rooms: IRoom[] | undefined,
  eventMaps?: IMap[],
): ITeleportPreview[] => {
  if (!rooms?.length) {
    return [];
  }

  const previews: ITeleportPreview[] = [];
  rooms.forEach((room) => {
    const map = resolveRoomMap(room, eventMaps);
    if (map?.teleportPreviews?.length) {
      previews.push(
        ...map.teleportPreviews.filter((preview) => preview.showInUi !== false),
      );
    }
  });

  return previews.sort(
    (a, b) => (a.positionInWidget ?? 0) - (b.positionInWidget ?? 0),
  );
};

export const useTeleportPreviews = () => {
  const { currentEvent, rooms, currentRoom } = useMytaverseEvent();
  const eventMaps = currentEvent?.maps;

  const teleportPreviews = useMemo<ITeleportPreview[]>(() => {
    const fromEventRooms = collectTeleportPreviews(
      currentEvent?.rooms,
      eventMaps,
    );
    if (fromEventRooms.length > 0) {
      return fromEventRooms;
    }

    const fromEventMaps = collectTeleportPreviewsFromMaps(eventMaps);
    if (fromEventMaps.length > 0) {
      return fromEventMaps;
    }

    const fromRoomsState = collectTeleportPreviews(rooms, eventMaps);
    if (fromRoomsState.length > 0) {
      return fromRoomsState;
    }

    if (currentRoom) {
      const map = resolveRoomMap(currentRoom, eventMaps);
      if (map?.teleportPreviews?.length) {
        return collectTeleportPreviewsFromMaps([map]);
      }
    }

    return [];
  }, [currentEvent?.rooms, eventMaps, rooms, currentRoom]);

  return {
    teleportPreviews,
    hasMapTeleportPreviews: teleportPreviews.length > 0,
  };
};

export const useTeleportOpen = () => {
  const { open, setOpen } = useTeleportModal();
  const { hasMapTeleportPreviews } = useTeleportPreviews();
  const { sendMessageToUnreal } = useMytaverse();
  const {
    isTeleportingToRoomByUnreal,
    setIsTeleportingToRoomByUnreal,
    trackEventAnalytics,
  } = useMytaverseEvent();
  const { setHideCurrentlyOpenOverlays } = useOverlayContext();

  const closeTeleport = useCallback(() => {
    setOpen(false);
    setIsTeleportingToRoomByUnreal(false);
    trackEventAnalytics(AnalyticsType.TeleportModalClose);
  }, [setOpen, setIsTeleportingToRoomByUnreal, trackEventAnalytics]);

  const showReactTeleportModal = useCallback(() => {
    setOpen(true);
    setIsTeleportingToRoomByUnreal(true);
    trackEventAnalytics(AnalyticsType.TeleportModalOpen);
  }, [setOpen, setIsTeleportingToRoomByUnreal, trackEventAnalytics]);

  const openTeleport = useCallback(
    (options?: { fromEngine?: boolean }) => {
      if (hasMapTeleportPreviews) {
        if (useTeleportModal.getState().open) {
          return;
        }

        teleportOpenGuard.suppressHideClose = true;
        showReactTeleportModal();
        setHideCurrentlyOpenOverlays(true);
        queueMicrotask(() => {
          setHideCurrentlyOpenOverlays(false);
          teleportOpenGuard.suppressHideClose = false;
        });
        return;
      }

      if (options?.fromEngine) {
        setIsTeleportingToRoomByUnreal(true);
        trackEventAnalytics(AnalyticsType.TeleportModalOpen);
        return;
      }

      setOpen(false);
      sendMessageToUnreal({ action: WebsocketAction.BeamMeUp });
      setIsTeleportingToRoomByUnreal(true);
      trackEventAnalytics(AnalyticsType.TeleportModalOpen);
    },
    [
      hasMapTeleportPreviews,
      setHideCurrentlyOpenOverlays,
      showReactTeleportModal,
      setOpen,
      setIsTeleportingToRoomByUnreal,
      trackEventAnalytics,
      sendMessageToUnreal,
    ],
  );

  const toggleTeleport = useCallback(
    (options?: { fromEngine?: boolean }) => {
      const isOpen = useTeleportModal.getState().open;

      if (hasMapTeleportPreviews) {
        if (isOpen) {
          closeTeleport();
          return;
        }

        if (shouldDebounceTeleportOpen()) {
          return;
        }

        openTeleport(options);
        return;
      }

      if (isTeleportingToRoomByUnreal) {
        closeTeleport();
        return;
      }

      openTeleport(options);
    },
    [
      hasMapTeleportPreviews,
      isTeleportingToRoomByUnreal,
      closeTeleport,
      openTeleport,
    ],
  );

  return {
    open,
    hasMapTeleportPreviews,
    openTeleport,
    closeTeleport,
    toggleTeleport,
  };
};
